































































import Component, { mixins } from "vue-class-component";
import { InputFile } from "@scrile/api-provider/dist/api/FileProvider";
import { QuestionaryInfo } from "@scrile/api-provider/dist/api/QuestionaryInfoProvider";
import { UserVerification } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { getFileMimeType } from "@scrile/tools/dist/lib/FileHelpers";
import WithAuthUser from "@/mixins/WithAuthUser";
import WithFiles from "@/mixins/WithFiles";
import WithUserVerification from "@/mixins/WithUserVerification";
import providers from "@/lib/api";
import FileUploader, { showDefaultUploadError } from "@/lib/FileUploader";
import config from "@/config";
import { FormValues } from "@/types";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseForm from "@/components/BaseForm";
import BaseInputFile from "@/components/BaseInputFile/BaseInputFile.vue";
import LoadingThumbnail from "@/components/ContentLoadingPlaceholders/components/LoadingThumbnail.vue";
import PayeeInfoPreview from "@/views/PagePayeeInfo/components/PayeeInfoPreview.vue";
import PayeeInfoInputFile from "./PayeeInfoInputFile.vue";
import { personalIdFields } from "../config";

@Component({
  name: "PersonalIdForm",
  components: { PayeeInfoPreview, BaseInputFile, LoadingThumbnail, BaseCheckbox, PayeeInfoInputFile, BaseForm },
})
export default class PersonalIdForm extends mixins(WithAuthUser, WithUserVerification, WithFiles) {
  public userVerificationStatus: UserVerification | null = null;

  public questionaryInfo: QuestionaryInfo | null = null;

  public sensitiveContent = false;

  public progress: Record<string, { progress: number; isLoading: boolean }> = {
    id_photo: {
      progress: 100,
      isLoading: false,
    },
    photo_with_id: {
      progress: 100,
      isLoading: false,
    },
  };

  public get fields() {
    return personalIdFields();
  }

  public get values(): FormValues {
    return {
      ...(config.disableAdult ? {} : { sensitiveContent: this.sensitiveContent }),
      id_photo: {
        data: this.userVerificationStatus?.idPhoto || null,
      },
      photo_with_id: {
        data: this.userVerificationStatus?.photoWithId || null,
      },
    };
  }

  public inputSensitive(v: boolean) {
    this.sensitiveContent = v;
    this.checkValid();
  }

  public async addFileToQueue(type: string, file: File): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      const response = await providers.FileProvider.getUploadUrl({
        fileIds: [{ subjectId: this.authUserStrict.id, type }],
      });

      FileUploader.addToQueue([
        {
          file: file,
          fileType: getFileMimeType(file),
          id: `${response[0].fileId.subjectId}_${response[0].fileUpload.uuid}`,
          urlPart: response[0].fileUpload.urlPart,
          onSuccess: async () => {
            this.progress[type].isLoading = false;
            this.progress[type].progress = 100;
            resolve(response[0]);
          },
          onUploadProgress: progress => {
            const res = ((progress?.loaded ?? 0) / (progress?.total ?? 0)) * 100;
            this.progress[type].progress = res === 100 ? 99.9 : res;
          },
          onError: e => {
            showDefaultUploadError();
            reject(e);
          },
        },
      ]);
    });
  }

  public async upload(file: FileList | null, type: string) {
    if (file) {
      this.progress[type].progress = 0;
      this.progress[type].isLoading = true;
      const uploadData = await this.addFileToQueue(type, file[0]);
      const data: InputFile = {
        fileId: uploadData.fileId,
        uuid: uploadData.fileUpload.uuid,
      };
      await providers.FileProvider.update({ data });
      await this.getUserVerification();
    } else {
      await this.filesDelete({ fileId: { subjectId: this.authUserStrict.id, type: type } });
      await this.getUserVerification();
    }
    this.checkValid();
  }

  public checkValid() {
    const container = this.fields.find(el => el.name === "photo_container");

    if (!config.disableAdult && !this.sensitiveContent) {
      return this.$emit("setValid", false);
    }
    if (!container) return this.$emit("setValid", true);

    if (
      container.value.find((i: { name: string }) => i.name === "id_photo")?.meta?.required &&
      !this.userVerificationStatus?.idPhoto
    ) {
      return this.$emit("setValid", false);
    }
    if (
      container.value.find((i: { name: string }) => i.name === "photo_with_id")?.meta?.required &&
      !this.userVerificationStatus?.photoWithId
    ) {
      return this.$emit("setValid", false);
    }
    return this.$emit("setValid", true);
  }

  public async getUserVerification() {
    let data = await this.userVerificationSearch({ userIds: [this.authUserStrict.id] });
    this.userVerificationStatus = data.result.find(i => i.userId === this.authUserStrict.id) || null;
  }

  public async getData() {
    await Promise.all([
      this.getUserVerification(),
      providers.QuestionaryInfoProvider.find({ id: this.authUserStrict.id }).then(r => {
        this.questionaryInfo = r;
        this.sensitiveContent = r.fields.sensitiveContent as boolean;
      }),
    ]);
    this.checkValid();
  }

  public async submitSensitiveInfo() {
    const data: QuestionaryInfo = {
      fields: { sensitiveContent: this.sensitiveContent, userId: this.authUserStrict.id },
    };
    this.questionaryInfo = await providers.QuestionaryInfoProvider.update({ data });
  }

  public async created() {
    this.$emit("setValid", true);
    this.$emit("setCb", () => this.submitSensitiveInfo());
    try {
      this.$emit("setLoading", true);
      await this.getData();
    } finally {
      this.$emit("setLoading", false);
    }
  }
}
